import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Empty, Spin, message, Drawer, Modal, Select, Tag, Checkbox, InputNumber, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useDVIInspection } from '../../hooks/useDVIInspection';
import {
  DVIInspectionForm, InspectionTabLabel, DVITapToConvertModal,
} from '../DVI';
import InspectionStatusCard from '../DVI/InspectionStatusCard';
import UnassignedPhotoTray from '../DVI/UnassignedPhotoTray';
import { dviTemplateApi, dviApi } from '../../services/api/dviApi';
import type { DVIInspectionItem } from '../../types/dvi';
import { PlusOutlined, CalendarOutlined } from '@ant-design/icons';
import { WorkOrder, WorkOrderLineItem } from '../../types';
import { LineItemList } from '../LineItems';
import PartsToOrderModal from './PartsToOrderModal';
import SendWorkOrderModal from './SendWorkOrderModal';
import RelayEmailModal from '../Shared/RelayEmailModal';
import { useRelayEmail } from '../../hooks/useRelayEmail';
import PaymentModal from '../Payment/PaymentModal';
import PaymentLinkModal from '../Payment/PaymentLinkModal';
import PaymentHistory from '../Payment/PaymentHistory';
import InvoiceDetailModal from '../Invoice/InvoiceDetailModal';
import SMSConversationThread from '../SMS/SMSConversationThread';
import { ActivityLogComponent } from '../ActivityLog/ActivityLog';
import api, { workOrderApi, estimateApi } from '../../services/api';
import { invoiceService } from '../../services/invoiceService';
import { Invoice } from '../../services/paymentService';
import { InvoicePDFService } from '../../services/invoicePdfService';
import { ApprovalPDFService } from '../../services/approvalPdfService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useHasRole, useCapability } from '../auth/RoleGuard';
import WaiveDiagnosisModal from './WaiveDiagnosisModal';
import { socketService } from '../../services/socketService';
import { PDFSettingsApiService, PDFSettings } from '../../services/pdfSettingsApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import WorkOrderHeader from './WorkOrderHeader';
import WorkOrderFormFields from './WorkOrderFormFields';
import CustomerCompletenessPanel from '../CustomerCompletenessPanel';
import WorkOrderActions from './WorkOrderActions';
import WorkOrderFinancials, { PricingData } from './WorkOrderFinancials';
import { computeWorkOrderTotals } from '../../utils/workOrderTotals';
import { assetLabel } from '../../utils/assetLabel';
import { QuickCatalogLinks } from '../Shared/QuickCatalogLinks';
import AuditTrailTimeline from '../Shared/AuditTrailTimeline';
import { EstimateApprovalReview } from '../Approval/EstimateApprovalReview';
import SendDialog from '../Shared/SendDialog';
import AiLookupPanel from './AiLookupPanel';
import { DiagnosticsTab } from '../Diagnostics';
import DtcLookupCard from './DtcLookupCard';
import DtcList from './DtcList';
import DiagnosisC3Table from './DiagnosisC3Table';
import { PerJobProfit } from '../Money';
import { QuickBookingDrawer } from '../SchedulingHub/QuickBookingDrawer';
import { BREAKPOINTS } from '../../constants/breakpoints';
import { useAiFeatureFlags } from '../../hooks/useAiFeatureFlags';

// AiLookupPanel, DiagnosticsTab, DtcLookupCard/DtcList/DiagnosisC3Table are gated
// at runtime via useAiFeatureFlags() (migration 195 per-shop AI settings) — see
// AI_LOOKUP_ENABLED / DIAG_ENABLED / DTC_ENABLED below, computed inside the
// component. This replaces the old build-time-only REACT_APP_AI_LOOKUP /
// REACT_APP_DIAGNOSTIC_PROCEDURES / REACT_APP_DTC_DIAGNOSIS env flags, which
// were unset in this deployment and gave the owner no way to turn them on.

const getRolePrefix = (userRole?: string): string =>
  userRole === 'customer' ? '/portal' :
  userRole === 'technician' ? '/technician' :
  userRole === 'advisor' ? '/advisor' :
  userRole === 'owner' ? '/owner' : '/admin';

const WO_TYPE_BADGE: Record<string, { color: string; label: string }> = {
  diagnosis: { color: 'purple', label: 'Diagnosis' },
  repair: { color: 'geekblue', label: 'Repair' },
};

const OptimizedWorkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const canSeeCost = useHasRole(['owner', 'advisor']);
  const canApplyDiscounts = useCapability('apply_discounts');
  const canBypassInspection = useCapability('bypass_inspection');
  // Diagnosis capability: owner role OR explicit manage_diagnosis permission.
  const canManageDiagnosis = user?.role === 'owner'
    || (Array.isArray(profile?.permissions) && profile!.permissions!.includes('manage_diagnosis'));

  // Scheduling capability: owner role OR explicit manage_schedule permission.
  const canSchedule = user?.role === 'owner'
    || (Array.isArray(profile?.permissions) && profile!.permissions!.includes('manage_schedule'));
  const rolePrefix = getRolePrefix(user?.role);
  // Runtime AI feature toggles (migration 195) — replaces the old build-time
  // REACT_APP_AI_LOOKUP / REACT_APP_DIAGNOSTIC_PROCEDURES / REACT_APP_DTC_DIAGNOSIS
  // env flags. Server is the source of truth; these only decide what renders.
  const { aiLookupEnabled: AI_LOOKUP_ENABLED, diagnosticProceduresEnabled: DIAG_ENABLED, dtcDiagnosisEnabled: DTC_ENABLED } = useAiFeatureFlags();
  const { subscribeToEntity, unsubscribeFromEntity } = useNotifications();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  // Bumped by the header "Add" button to open LineItemList's inline "new"
  // editor from outside — see Task 12 (Drawer line-item form retired).
  const [addNonce, setAddNonce] = useState(0);
  const isMobile = useIsMobile();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < BREAKPOINTS.xl);
  const [isPartsModalVisible, setIsPartsModalVisible] = useState(false);
  const [sendWorkOrderModalVisible, setSendWorkOrderModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentLinkModalVisible, setPaymentLinkModalVisible] = useState(false);
  const [paymentHistoryVisible, setPaymentHistoryVisible] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [invoiceRelayOpen, setInvoiceRelayOpen] = useState(false);
  const [invoiceRelaySending, setInvoiceRelaySending] = useState(false);
  const { defaultEmail: relayEmail } = useRelayEmail();
  const [forceUnlockMode, setForceUnlockMode] = useState(false);
  const [unapproveModalVisible, setUnapproveModalVisible] = useState(false);
  const [showBulkInPersonApproval, setShowBulkInPersonApproval] = useState(false);
  // Diagnostic prelink: set when a line-item "+ Diagnostic" button is tapped.
  // Consumed by DiagnosticsTab which opens ProcedurePicker pre-linked to that line.
  const [prelinkLineId, setPrelinkLineId] = useState<number | null>(null);
  // DTC refresh counter — incremented after a DTC is attached so DtcList reloads.
  const [dtcRefreshTick, setDtcRefreshTick] = useState(0);
  // Diagnosis loop: spawn-repair modal state.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [spawnRepairOpen, setSpawnRepairOpen] = useState(false);
  const [spawnSelectedIds, setSpawnSelectedIds] = useState<number[]>([]);
  const [spawning, setSpawning] = useState(false);
  const [woTypeSaving, setWoTypeSaving] = useState(false);
  const [waiveDiagnosisItem, setWaiveDiagnosisItem] = useState<WorkOrderLineItem | null>(null);
  const [waiving, setWaiving] = useState(false);

  // DVI Inspection state
  const workOrderNumericId = parseInt(id ?? '0') || 0;
  const { inspection, loading: dviLoading, error: dviError, refetch: refetchDVI, updateItem: dviUpdateItem, complete: dviComplete } = useDVIInspection(workOrderNumericId);
  const [dviTemplates, setDVITemplates] = useState<Array<{id: number; name: string}>>([]);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [pickedTemplateId, setPickedTemplateId] = useState<number | undefined>();
  const [convertItem, setConvertItem] = useState<DVIInspectionItem | null>(null);
  // Condensed summary is the DEFAULT inspection view; the full editor opens on demand
  // (including for completed inspections, so items can be edited/added later).
  const [dviEditorOpen, setDviEditorOpen] = useState(false);

  const canEditTaxRate = user?.role === 'owner' || user?.role === 'advisor';

  const pricing: PricingData = useMemo(() => computeWorkOrderTotals(
    workOrder?.line_items as import('../../types').WorkOrderLineItem[] | undefined,
    workOrder?.tax_rate ?? 7, // mirrors backend default of 7 when unset; `??` keeps an explicit 0
    (workOrder?.discount_type != null && workOrder?.discount_value != null)
      ? { type: workOrder.discount_type as 'percentage' | 'amount' | 'final_price', value: workOrder.discount_value }
      : undefined,
    workOrder?.amount_paid ?? 0,
  ), [workOrder]);

  // Edit guard: prevent WebSocket updates from resetting form state during editing
  const isEditingRef = useRef(false);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startEditing = useCallback(() => { isEditingRef.current = true; }, []);
  const stopEditing = useCallback(() => {
    isEditingRef.current = false;
    if (pendingUpdateRef.current) { clearTimeout(pendingUpdateRef.current); pendingUpdateRef.current = null; loadWorkOrder(); loadInvoice(); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setIsEditing = useCallback((editing: boolean) => { if (editing) startEditing(); else stopEditing(); }, [startEditing, stopEditing]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < BREAKPOINTS.xl);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { loadWorkOrder(); loadInvoice(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    subscribeToEntity('work_order', parseInt(id));
    const handleEntityUpdate = () => {
      const activeEl = document.activeElement;
      const isTypingInline = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA');
      if (isEditingRef.current || isTypingInline) {
        if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = setTimeout(() => { if (!isEditingRef.current) { loadWorkOrder(); loadInvoice(); } }, 2000);
        return;
      }
      loadWorkOrder(); loadInvoice();
      message.info({ content: 'Work order updated', duration: 2, style: { marginTop: '60px' } });
    };
    socketService.on('entity_updated', handleEntityUpdate);
    return () => { unsubscribeFromEntity('work_order', parseInt(id)); socketService.off('entity_updated', handleEntityUpdate); if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current); };
  }, [id, subscribeToEntity, unsubscribeFromEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chrome-extension dispatch subscription (C3-T19 review) — join entity channel,
  // listen for dispatch_items_added, re-load work order so newly inserted pending
  // line items show up. Also emit window.postMessage so the extension knows the
  // current target entity.
  useEffect(() => {
    if (!id) return;
    const workOrderId = parseInt(id);
    socketService.joinEntityChannel('work_order', workOrderId);
    window.postMessage(
      { type: 'cnf_target', entity: { type: 'work_order', id: workOrderId } },
      window.location.origin
    );
    const unsubscribe = socketService.onDispatchItemsAdded((evt) => {
      if (evt.entity_type === 'work_order' && evt.entity_id === workOrderId) {
        message.success(`Imported ${evt.count} item${evt.count === 1 ? '' : 's'} from ${evt.source}`);
        loadWorkOrder();
      }
    });
    return () => { unsubscribe(); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkOrder = useCallback(async () => {
    try { const response = await api.get(`/work-orders/${id}`); setWorkOrder(response.data); }
    catch (error) { message.error('Failed to load work order'); console.error('Error loading work order:', error); }
    finally { setInitialLoading(false); }
  }, [id]);

  const loadInvoice = useCallback(async () => {
    try { const invoiceData = await invoiceService.getInvoiceByWorkOrderId(parseInt(id!)); setInvoice(invoiceData); }
    catch (error) { console.error('Error loading invoice:', error); }
  }, [id]);

  const handleFieldSave = useCallback(async (field: string, value: any) => {
    await workOrderApi.update(workOrder!.id!, { [field]: value } as any); await loadWorkOrder();
  }, [workOrder, loadWorkOrder]);

  // Multi-field save in a single PUT (tech picker saves id + name together).
  const handleFieldsSave = useCallback(async (fields: Record<string, any>) => {
    await workOrderApi.update(workOrder!.id!, fields as any); await loadWorkOrder();
  }, [workOrder, loadWorkOrder]);

  const handleBulkInPersonApproval = useCallback(async (staffName: string, notes: string, confirmed: boolean) => {
    try {
      await api.post(`/work-orders/${workOrder!.id}/approve-all-in-person`, { staff_name: staffName, notes, confirmed });
      message.success('All items approved in-person'); setShowBulkInPersonApproval(false); loadWorkOrder();
    } catch (err: any) { message.error(err.response?.data?.error || 'Approval failed'); }
  }, [workOrder, loadWorkOrder]);

  const handleAddLineItem = () => { setAddNonce((n) => n + 1); startEditing(); };

  const handlePaymentSuccess = async () => { setPaymentModalVisible(false); await loadWorkOrder(); await loadInvoice(); message.success('Payment recorded successfully'); };

  const handleDeleteLineItem = async (lineItemId: number) => {
    try { await api.delete(`/work-orders/${id}/line-items/${lineItemId}`); await loadWorkOrder(); message.success('Line item deleted successfully'); }
    catch (error) { message.error('Failed to delete line item'); console.error('Error deleting line item:', error); }
  };

  const handleTaxRateChange = useCallback(async (rate: number) => {
    try { await api.patch(`/work-orders/${id}/tax-rate`, { tax_rate: rate }); await loadWorkOrder(); message.success('Tax rate updated successfully'); }
    catch (error: any) { message.error(error.response?.data?.error || 'Failed to update tax rate'); }
  }, [id, loadWorkOrder]);

  // Diagnosis loop — change wo_type (owner / manage_diagnosis only; backend re-enforces).
  const handleWoTypeChange = useCallback(async (woType: string) => {
    setWoTypeSaving(true);
    try {
      await api.patch(`/work-orders/${id}/type`, { wo_type: woType });
      await loadWorkOrder();
      message.success('Work order type updated');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update work order type');
    } finally {
      setWoTypeSaving(false);
    }
  }, [id, loadWorkOrder]);

  // Diagnosis loop — open Create Repair Order modal, default-check approved lines.
  const openSpawnRepair = useCallback(() => {
    const approvedIds = (workOrder?.line_items || [])
      .filter((li) => li.customer_approval_status === 'approved')
      .map((li) => li.id!)
      .filter((v) => v != null);
    setSpawnSelectedIds(approvedIds);
    setSpawnRepairOpen(true);
  }, [workOrder]);

  const handleSpawnRepair = useCallback(async () => {
    if (spawnSelectedIds.length === 0) { message.warning('Select at least one line item'); return; }
    setSpawning(true);
    try {
      const res = await api.post(`/work-orders/${id}/spawn-repair`, { lineItemIds: spawnSelectedIds });
      const newId = res.data?.id;
      setSpawnRepairOpen(false);
      message.success('Repair order created');
      if (newId) navigate(`${rolePrefix}/work-orders/${newId}`);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create repair order');
    } finally {
      setSpawning(false);
    }
  }, [id, spawnSelectedIds, navigate, rolePrefix]);

  /**
   * Quote the repair instead of building it.
   *
   * `spawn-repair` creates a work order directly — right when the customer is
   * standing there and agrees on the spot, wrong when they need to see a price
   * first. This produces an ESTIMATE carrying the diagnosis they already paid for
   * as a credit, which they can be sent and can approve; the credit rides through
   * the conversion so they are not charged for the diagnosis twice.
   */
  const handleSpawnRepairEstimate = useCallback(async () => {
    if (spawnSelectedIds.length === 0) { message.warning('Select at least one line item'); return; }
    setSpawning(true);
    try {
      const res = await api.post(`/work-orders/${id}/spawn-repair-estimate`, { lineItemIds: spawnSelectedIds });
      const newId = res.data?.id;
      setSpawnRepairOpen(false);
      message.success('Repair estimate created');
      if (newId) navigate(`${rolePrefix}/estimates/${newId}`);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create the repair estimate');
    } finally {
      setSpawning(false);
    }
  }, [id, spawnSelectedIds, navigate, rolePrefix]);

  /**
   * Mark (or unmark) a line as the diagnosis fee.
   *
   * This is what the credit is computed from — `spawn-repair` and
   * `spawn-repair-estimate` both sum the `is_diagnosis` lines. Until this existed
   * nothing could set the flag, so every credit came out $0 and a customer who paid
   * for a diagnosis was charged for it again on the repair.
   *
   * Sends only the flag: the line-item PUT recalculates labor from whatever it
   * receives, so resending hours/rate here would re-derive money as a side effect
   * of a bookkeeping change.
   */
  const handleToggleDiagnosisLine = useCallback(async (item: WorkOrderLineItem, isDiagnosis: boolean) => {
    try {
      await api.put(`/work-orders/${id}/line-items/${item.id}`, { is_diagnosis: isDiagnosis });
      await loadWorkOrder();
      message.success(isDiagnosis ? 'Marked as the diagnosis fee' : 'No longer the diagnosis fee');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Could not update that line');
    }
  }, [id, loadWorkOrder]);

  const handleJobDiscountSave = useCallback(async (type: string | null, value: number | null) => {
    try { await workOrderApi.update(workOrder!.id!, { discount_type: type, discount_value: value } as any); loadWorkOrder(); message.success('Discount updated'); }
    catch { message.error('Failed to update discount'); }
  }, [workOrder, loadWorkOrder]);

  const handleApplyPromo = useCallback(async (code: string) => {
    try { await workOrderApi.applyPromo(workOrder!.id!, code); await loadWorkOrder(); message.success('Promo code applied'); }
    catch (error: any) {
      if (error?.response?.status === 400) message.error('Invalid or expired promo code');
      else message.error('Failed to apply promo code');
    }
  }, [workOrder, loadWorkOrder]);

  const handleRemovePromo = useCallback(async () => {
    try { await workOrderApi.removePromo(workOrder!.id!); await loadWorkOrder(); message.success('Promo code removed'); }
    catch { message.error('Failed to remove promo code'); }
  }, [workOrder, loadWorkOrder]);

  const handleConfirmWaiveDiagnosis = useCallback(async (repairLineItemIds: number[], distributionMethod: 'full' | 'split') => {
    if (!waiveDiagnosisItem?.id) return;
    setWaiving(true);
    try {
      await api.post(`/work-orders/${id}/line-items/${waiveDiagnosisItem.id}/waive-diagnosis`, {
        repairLineItemIds,
        distributionMethod,
        userId: user?.id,
      });
      setWaiveDiagnosisItem(null);
      await loadWorkOrder();
      message.success('Diagnosis fee waived');
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Failed to waive diagnosis fee');
    } finally {
      setWaiving(false);
    }
  }, [id, waiveDiagnosisItem, user, loadWorkOrder]);

  const handleForceUnlock = useCallback(() => { setForceUnlockMode(true); message.success('Work order unlocked for editing. Invoice remains unchanged.'); }, []);
  const handleForceDelete = useCallback(async () => {
    try { message.loading({ content: 'Deleting work order...', key: 'forceDelete' }); await api.delete(`/work-orders/${id}/force`); message.success({ content: 'Work order deleted successfully', key: 'forceDelete' }); navigate('/admin/work-orders'); }
    catch (error: any) { message.error({ content: error.response?.data?.error || 'Failed to delete work order', key: 'forceDelete' }); }
  }, [id, navigate]);

  const handleUnapproveWorkOrder = useCallback(async (reason: string) => {
    try { await api.post(`/work-orders/${id}/unapprove`, { unapprove_reason: reason }); message.success('Work order unapproved and deleted successfully'); navigate('/admin/work-orders'); }
    catch (error: any) { message.error(error.response?.data?.error || 'Failed to unapprove work order'); }
  }, [id, navigate]);

  const fetchPdfSettings = async () => { try { return await PDFSettingsApiService.getDefaultSettings(); } catch { return null; } };
  const hydrateInvoice = useCallback((wo: WorkOrder, inv: Invoice | null): Invoice => {
    const billable = wo.line_items?.filter(i => i.customer_approval_status === 'approved') || [];
    const lineItems = billable.map((item) => {
      const lt = (item.labor_hours || 0) * (item.labor_rate || 0);
      const pt = (item.parts || []).reduce((s, p) => s + p.quantity * p.unit_price, 0);
      return { id: item.id, invoice_id: inv?.id || 0, line_number: item.line_number, concern: item.concern, cause: item.cause, correction: item.correction, labor_hours: item.labor_hours, labor_rate: item.labor_rate, labor_discount_percentage: item.labor_discount_percentage, labor_discount_amount: item.labor_discount_amount, labor_total: item.labor_total || lt, parts_total: item.parts_total || pt, line_total: item.line_total || (lt + pt), is_diagnosis: item.is_diagnosis || false, is_diagnosis_waived: item.is_diagnosis_waived || false, diagnosis_waiver_amount: item.diagnosis_waiver_amount, original_line_total: item.original_line_total, notes: item.notes, customer_notes: item.customer_notes, created_at: item.created_at, parts: item.parts?.map(p => ({ id: p.id, invoice_line_item_id: 0, part_name: p.part_name, part_number: p.part_number, quantity: p.quantity, unit_price: p.unit_price, discount_percentage: p.discount_percentage, discount_amount: p.discount_amount, discounted_price: p.discounted_price, total_price: p.total_price, notes: p.notes, created_at: p.created_at })) || [] };
    });
    return { ...(inv || {}), id: inv?.id, invoice_number: inv?.invoice_number || `INV-${new Date().getFullYear()}-${wo.wo_number}`, work_order_id: wo.id!, customer_id: wo.customer_id, vehicle_id: wo.vehicle_id, invoice_date: inv?.invoice_date || new Date().toISOString(), subtotal: wo.subtotal || 0, tax_rate: wo.tax_rate, tax_amount: wo.tax_amount || 0, total_amount: wo.total_amount || 0, amount_paid: wo.amount_paid || 0, status: inv?.status || 'draft', is_archived: inv?.is_archived || false, line_items: lineItems, customer: wo.customer, vehicle: wo.vehicle, payments: inv?.payments, notes: inv?.notes, payment_method: inv?.payment_method } as Invoice;
  }, []);

  const handleDownloadInvoicePDF = useCallback(async () => {
    if (!invoice && !workOrder) { message.error('No invoice or work order available'); return; }
    try { message.loading({ content: 'Generating PDF...', key: 'pdfDownload' }); const ps = await fetchPdfSettings(); const fi = workOrder ? hydrateInvoice(workOrder, invoice) : invoice; if (!fi) throw new Error('Could not generate invoice data'); await InvoicePDFService.generateInvoicePDF(fi, ps ? (PDFSettingsApiService.mapToLayoutOptions(ps) as any) : undefined, ps ? PDFSettingsApiService.mapToCompanyInfo(ps) : undefined); message.success({ content: 'PDF downloaded successfully', key: 'pdfDownload' }); }
    catch (error) { console.error('Error generating PDF:', error); message.error({ content: 'Failed to generate PDF', key: 'pdfDownload' }); }
  }, [invoice, workOrder, hydrateInvoice]);

  const handleDownloadApprovalPDF = useCallback(async () => {
    if (!workOrder?.estimate_id) { message.error('No linked estimate found'); return; }
    try { message.loading({ content: 'Generating approval PDF...', key: 'approvalPdf' }); const response = await estimateApi.getById(workOrder.estimate_id); const ps = await fetchPdfSettings(); await ApprovalPDFService.generateApprovalPDF(response.data, {}, ps ? PDFSettingsApiService.mapToCompanyInfo(ps) : undefined); message.success({ content: 'PDF downloaded!', key: 'approvalPdf' }); }
    catch (err) { console.error('Error generating approval PDF:', err); message.error({ content: 'Failed to generate PDF', key: 'approvalPdf' }); }
  }, [workOrder]);

  const handleSendInvoiceEmail = useCallback(async () => {
    if (!invoice) { message.error('No invoice available'); return; }
    if (!workOrder?.customer?.email) { message.error('Customer email not found'); return; }
    try { message.loading({ content: 'Generating and sending invoice...', key: 'emailSend' }); const pdfBuffer = await InvoicePDFService.generateInvoicePDFBuffer(invoice); await invoiceService.sendInvoiceEmail({ invoiceId: invoice.id!, pdfBuffer, customerEmail: workOrder.customer.email, customerName: workOrder.customer.company_name || `${workOrder.customer.first_name} ${workOrder.customer.last_name}` }); message.success({ content: 'Invoice sent successfully!', key: 'emailSend' }); }
    catch (error: any) { console.error('Error sending invoice:', error); message.error({ content: error.response?.data?.error || 'Failed to send invoice', key: 'emailSend' }); }
  }, [invoice, workOrder]);

  // Email the invoice to the owner's own inbox (relay address, editable per-send)
  // so they can forward / text it to the customer without a paid SMS gateway.
  const doSendInvoiceToSelf = useCallback(async (address: string) => {
    if (!invoice) { message.error('No invoice available'); return; }
    setInvoiceRelaySending(true);
    try {
      message.loading({ content: 'Generating invoice…', key: 'invSelf' });
      const pdfBuffer = await InvoicePDFService.generateInvoicePDFBuffer(invoice);
      await invoiceService.sendInvoiceEmail({ invoiceId: invoice.id!, pdfBuffer, customerEmail: address, customerName: 'CNF Auto LLC' });
      message.success({ content: 'Invoice emailed to you', key: 'invSelf' });
      setInvoiceRelayOpen(false);
    } catch (error: any) {
      message.error({ content: error.response?.data?.error || error.message || 'Failed to send invoice', key: 'invSelf' });
    } finally {
      setInvoiceRelaySending(false);
    }
  }, [invoice]);

  // DVI handlers
  const handleStartDVI = async () => {
    try {
      const list = await dviTemplateApi.list();
      setDVITemplates(list.map(t => ({ id: t.id, name: t.name })));
      if (list.length === 1) {
        await dviApi.start(workOrderNumericId, list[0].id);
        await refetchDVI();
        setDviEditorOpen(true); // jump straight into the editor after starting
      } else {
        setTemplatePickerOpen(true);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to start inspection');
    }
  };
  const handleConfirmStartDVI = async () => {
    if (!pickedTemplateId) return;
    try {
      await dviApi.start(workOrderNumericId, pickedTemplateId);
      setTemplatePickerOpen(false);
      await refetchDVI();
      setDviEditorOpen(true); // jump straight into the editor after starting
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to start inspection');
    }
  };
  const handleDVIConvert = async (input: any) => {
    if (!inspection || !convertItem) return;
    await dviApi.convertToLineItem(inspection.id, convertItem.id, input);
    setConvertItem(null);
    await refetchDVI();
  };

  if (initialLoading && !workOrder) return <div className="flex justify-center items-center min-h-screen bg-gray-50"><Spin size="large" /></div>;
  if (!workOrder) return <div className="flex justify-center items-center min-h-screen bg-gray-50"><Empty description="Work order not found" /></div>;

  const partsNeedingOrder = workOrder.line_items?.flatMap(item => item.parts || []).filter(part => part.status === 'needed') || [];
  const isLocked = !!invoice && !forceUnlockMode;
  const isOwner = user?.role === 'owner';

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkOrderActions invoice={invoice} forceUnlockMode={forceUnlockMode} isOwner={isOwner} invoiceNumber={invoice?.invoice_number} woNumber={workOrder.wo_number} pendingItemCount={workOrder.line_items?.filter((i: any) => i.customer_approval_status === 'pending').length || 0} pricingTotal={pricing.total} unapproveOpen={unapproveModalVisible} bulkApprovalOpen={showBulkInPersonApproval} onForceUnlock={handleForceUnlock} onForceDelete={handleForceDelete} onRelockWorkOrder={() => setForceUnlockMode(false)} onUnapprove={handleUnapproveWorkOrder} onBulkApproval={handleBulkInPersonApproval} onUnapproveClose={() => setUnapproveModalVisible(false)} onBulkApprovalClose={() => setShowBulkInPersonApproval(false)} />
      <WorkOrderHeader workOrder={workOrder} isMobile={isMobile} isLocked={isLocked} partsNeedingOrderCount={partsNeedingOrder.length} hasPendingApprovalItems={!!workOrder.line_items?.some((i: any) => i.customer_approval_status === 'pending')} onNavigateBack={() => navigate(-1)} onAddLineItem={handleAddLineItem} onSendToCustomer={() => setSendWorkOrderModalVisible(true)} onPartsModal={() => setIsPartsModalVisible(true)} onBulkApproval={() => setShowBulkInPersonApproval(true)} onUnapprove={() => setUnapproveModalVisible(true)} onTogglePublicLink={async (checked) => { const prev = workOrder.public_link_active; setWorkOrder({ ...workOrder, public_link_active: checked }); try { await workOrderApi.togglePublicLink(workOrder.id!, checked); } catch (error) { console.error('Error toggling public link:', error); setWorkOrder({ ...workOrder, public_link_active: prev }); message.error('Failed to update public link'); } }} inspection={inspection} inspectionLoaded={!dviLoading && !dviError} />
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: isMobile ? 12 : 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 380px', gap: isMobile ? 12 : 16, width: '100%' }}>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>
            {/*
              WP-02. Until now this screen had no way to edit the customer at all:
              reaching an address meant navigating to the customer page and losing
              whatever was in flight here. That is why work orders reach invoicing
              with no address on file.

              Measured against the `invoice` rule set and shown in the BLOCKING
              tone, because on a work order this is no longer advisory — the
              server returns 422 on POST /invoices and on every payment endpoint
              until it is filled. Better to say so here than to surprise someone
              at the moment they try to take money.
            */}
            <CustomerCompletenessPanel
              customerId={workOrder.customer_id ?? workOrder.customer?.id ?? null}
              vehicleId={workOrder.vehicle_id ?? workOrder.vehicle?.id ?? null}
              stage="invoice"
              tone="blocking"
              onSaved={() => loadWorkOrder()}
            />
            <WorkOrderFormFields
              workOrder={workOrder}
              isMobile={isMobile}
              isLocked={isLocked}
              onFieldSave={handleFieldSave}
              onFieldsSave={handleFieldsSave}
              setIsEditing={setIsEditing}
              onTaxRateChange={handleTaxRateChange}
            />
            {/* Diagnosis loop controls: badge, From-Diagnosis link, wo_type selector, Create Repair Order */}
            {(WO_TYPE_BADGE[workOrder.wo_type || 'standard'] || canManageDiagnosis || canSchedule || canEditTaxRate) && (
              <Card size="small" style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                  {WO_TYPE_BADGE[workOrder.wo_type || 'standard'] && (
                    <Tag color={WO_TYPE_BADGE[workOrder.wo_type!]!.color}>
                      {WO_TYPE_BADGE[workOrder.wo_type!]!.label}
                    </Tag>
                  )}
                  {workOrder.wo_type === 'repair' && workOrder.parent_diagnosis_wo_id && (
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate(`${rolePrefix}/work-orders/${workOrder.parent_diagnosis_wo_id}`)}
                    >
                      From Diagnosis #{workOrder.parent_diagnosis_wo_id}
                    </Button>
                  )}
                  {canManageDiagnosis && (
                    <Select
                      size="small"
                      value={workOrder.wo_type || 'standard'}
                      style={{ width: 150 }}
                      loading={woTypeSaving}
                      onChange={handleWoTypeChange}
                      options={[
                        { value: 'standard', label: 'Standard' },
                        { value: 'diagnosis', label: 'Diagnosis' },
                        { value: 'repair', label: 'Repair' },
                      ]}
                    />
                  )}
                  {canManageDiagnosis && workOrder.wo_type === 'diagnosis' && (
                    <Button type="primary" size="small" onClick={openSpawnRepair}>
                      Create Repair Order
                    </Button>
                  )}
                  {(canSchedule || canEditTaxRate) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#888' }}>Est. time on site (min)</span>
                      <InputNumber
                        size="small"
                        min={0}
                        step={15}
                        style={{ width: 110 }}
                        value={(workOrder as any).estimated_onsite_minutes ?? undefined}
                        onChange={(val) => handleFieldSave('estimated_onsite_minutes', val ?? null)}
                      />
                      <span style={{ fontSize: 12, color: '#888' }}>Parts ETA</span>
                      <DatePicker
                        size="small"
                        style={{ width: 140 }}
                        value={(workOrder as any).parts_eta_date ? dayjs((workOrder as any).parts_eta_date) : null}
                        onChange={(picked) => handleFieldSave('parts_eta_date', picked ? picked.format('YYYY-MM-DD') : null)}
                      />
                    </div>
                  )}
                  {canSchedule && (
                    <Button size="small" icon={<CalendarOutlined />} onClick={() => setScheduleOpen(true)} style={{ marginLeft: 'auto' }}>
                      Schedule
                    </Button>
                  )}
                </div>
              </Card>
            )}
            <LineItemList
              items={workOrder.line_items || []}
              isLocked={isLocked}
              isMobile={isMobile}
              workOrderId={workOrder.id}
              onDeleteItem={handleDeleteLineItem}
              inlineEditor
              isOwner={isOwner}
              onLineSaved={loadWorkOrder}
              addNonce={addNonce}
              onEditorDirtyChange={(dirty) => (dirty ? startEditing() : stopEditing())}
              onDiagnosticRequested={DIAG_ENABLED ? setPrelinkLineId : undefined}
              technicianId={user?.id}
              onWaiveDiagnosis={canApplyDiscounts ? setWaiveDiagnosisItem : undefined}
              onToggleDiagnosisLine={canManageDiagnosis ? handleToggleDiagnosisLine : undefined}
              financialSummary={{
                gross: pricing.laborSubtotal + pricing.partsSubtotal,
                totalDiscounts: Math.max(0, (pricing.laborSubtotal + pricing.partsSubtotal) - pricing.subtotal),
                jobDiscountAmount: pricing.jobDiscountAmount,
                jobDiscountLabel:
                  workOrder.discount_type === 'percentage' && workOrder.discount_value
                    ? `${workOrder.discount_value}%`
                    : undefined,
                subtotal: pricing.subtotal,
                taxRate: pricing.taxRate,
                taxAmount: pricing.taxAmount,
                total: pricing.total,
              }}
            />
            {/* C2-T25: Quick catalog links for parts lookup */}
            {workOrder.vehicle && (
              <div style={{ marginTop: 4 }}>
                <QuickCatalogLinks vehicle={workOrder.vehicle} />
              </div>
            )}

            {AI_LOOKUP_ENABLED && workOrder.line_items && workOrder.line_items.length > 0 && (
              <AiLookupPanel
                lineItemId={workOrder.line_items[0].id!}
                concern={workOrder.line_items[0].concern}
              />
            )}
            {DIAG_ENABLED && (
              <DiagnosticsTab
                workOrderId={workOrder.id!}
                vehicle={workOrder.vehicle ? {
                  year: workOrder.vehicle.year ?? 0,
                  make: workOrder.vehicle.make ?? '',
                  model: workOrder.vehicle.model ?? '',
                  engine_size: workOrder.vehicle.engine_size,
                } : undefined}
                prelinkLineItemId={prelinkLineId}
                onPrelinkConsumed={() => setPrelinkLineId(null)}
              />
            )}
            {DTC_ENABLED && (
              <>
                <DtcLookupCard
                  workOrderId={workOrder.id!}
                  vehicle={workOrder.vehicle ? {
                    year: workOrder.vehicle.year ?? 0,
                    make: workOrder.vehicle.make ?? '',
                    model: workOrder.vehicle.model ?? '',
                    engine_size: workOrder.vehicle.engine_size,
                  } : undefined}
                  onAttached={() => setDtcRefreshTick((t) => t + 1)}
                />
                <DtcList
                  workOrderId={workOrder.id!}
                  refreshTrigger={dtcRefreshTick}
                />
                <DiagnosisC3Table
                  workOrderId={workOrder.id!}
                />
              </>
            )}
            {/* DVI Inspection section — always rendered, shows Start CTA or form */}
            <Card
              style={{ marginTop: 8 }}
              title={<InspectionTabLabel inspection={inspection} />}
              size="small"
            >
              {!inspection ? (
                <div style={{ padding: '16px 0', textAlign: 'center' }}>
                  <Button type="primary" size="large" onClick={handleStartDVI}>
                    Start Inspection
                  </Button>
                </div>
              ) : !dviEditorOpen ? (
                /* Condensed summary — the DEFAULT view for both draft and completed */
                <>
                  <InspectionStatusCard inspection={inspection} />
                  <Button
                    type="primary"
                    block
                    size="large"
                    style={{ marginTop: 8, minHeight: 44 }}
                    onClick={() => setDviEditorOpen(true)}
                  >
                    {inspection.status === 'completed'
                      ? 'Reopen inspection (edit / add items)'
                      : 'Open full inspection'}
                  </Button>
                </>
              ) : (
                /* Full editor — reopenable even after completion */
                <>
                  <Button
                    block
                    size="large"
                    style={{ marginBottom: 8, minHeight: 44 }}
                    onClick={() => setDviEditorOpen(false)}
                  >
                    ▴ Collapse to summary
                  </Button>
                  <DVIInspectionForm
                    inspection={inspection}
                    canBypass={canBypassInspection}
                    onItemStatusChange={(itemId, status, notes) =>
                      dviUpdateItem(itemId, { status, notes })}
                    onSummaryChange={async (notes) => {
                      await dviApi.updateSummary(inspection.id, notes);
                    }}
                    onComplete={async () => {
                      try {
                        await dviComplete();
                        setDviEditorOpen(false); // back to the condensed summary
                      } catch (err: any) {
                        message.error(err?.response?.data?.error ?? 'Cannot complete');
                      }
                    }}
                    onConvert={(it) => setConvertItem(it)}
                    onReadingChange={(itemId, val) => {
                      dviApi.updateReading(inspection.id, itemId, val).then(() => refetchDVI());
                    }}
                    onAddItem={async (input) => {
                      await dviApi.addItem(inspection.id, input);
                      await refetchDVI();
                    }}
                  />
                  <UnassignedPhotoTray
                    inspectionId={inspection.id}
                    points={inspection.items.map(i => ({ id: i.id, point_name: i.point_name }))}
                    onAssigned={refetchDVI}
                  />
                </>
              )}
            </Card>
            {/* What the customer corrected at approval, and which corrections were
                taken. Read-only: a work order only exists once the submission
                reached `accepted` (estimates.ts /convert refuses otherwise), so
                there is nothing left to decide here. */}
            {workOrder.estimate_id && (
              <div style={{ marginTop: 8 }}>
                <EstimateApprovalReview estimateId={workOrder.estimate_id} readOnly />
              </div>
            )}
            <div style={{ marginTop: 8 }}><ActivityLogComponent entityType="work_order" entityId={workOrder.id!} height={400} /></div>
            {/* C2-T25: Audit trail timeline (status changes, sends, etc.) */}
            <div style={{ marginTop: 8 }}>
              <AuditTrailTimeline entityType="work-orders" entityId={workOrder.id!} />
            </div>
          </div>
          <div>
            <WorkOrderFinancials workOrder={workOrder} pricing={pricing} isMobile={isMobile} isLocked={isLocked} canSeeCost={canSeeCost} canEditTaxRate={canEditTaxRate} invoice={invoice} onTaxRateChange={handleTaxRateChange} onTaxRateCancel={() => {}} onJobDiscountSave={handleJobDiscountSave} onApplyPromo={handleApplyPromo} onRemovePromo={handleRemovePromo} onPaymentModal={() => setPaymentModalVisible(true)} onPaymentLinkModal={() => setPaymentLinkModalVisible(true)} onPaymentHistoryModal={() => setPaymentHistoryVisible(true)} onInvoiceModal={() => setInvoiceModalVisible(true)} onSendInvoiceEmail={handleSendInvoiceEmail} onSendInvoiceToSelf={() => setInvoiceRelayOpen(true)} onDownloadInvoicePDF={handleDownloadInvoicePDF} onDownloadApprovalPDF={handleDownloadApprovalPDF} startEditing={startEditing} stopEditing={stopEditing} signatureData={workOrder.signature_data} signedAt={workOrder.signed_at} signatureType={workOrder.signature_type} estimateId={workOrder.estimate_id} />
            {/* I-C: Per-job profit card — owner-only; PerJobProfit self-gates via useIsOwner() */}
            {workOrder.id && (
              <div style={{ marginTop: isMobile ? 12 : 16 }}>
                <PerJobProfit workOrderId={workOrder.id} />
              </div>
            )}
            {workOrder.customer?.phone && (<div style={{ marginTop: isMobile ? 12 : 16 }}><SMSConversationThread workOrderId={workOrder.id} customerId={workOrder.customer.id} customerPhone={workOrder.customer.phone} height={400} /></div>)}
          </div>
        </div>
      </div>
      <PartsToOrderModal visible={isPartsModalVisible} onClose={() => setIsPartsModalVisible(false)} workOrderId={workOrder.id!} parts={partsNeedingOrder} onUpdate={loadWorkOrder} />
      <RelayEmailModal
        open={invoiceRelayOpen}
        title="Email invoice to me"
        defaultEmail={relayEmail}
        sending={invoiceRelaySending}
        onSend={doSendInvoiceToSelf}
        onClose={() => setInvoiceRelayOpen(false)}
      />
      <SendWorkOrderModal visible={sendWorkOrderModalVisible} onClose={() => setSendWorkOrderModalVisible(false)} workOrderId={workOrder.id!} workOrderNumber={workOrder.wo_number} customerEmail={workOrder.customer?.email} customerPhone={workOrder.customer?.phone} onSent={() => { setSendWorkOrderModalVisible(false); loadWorkOrder(); }} />
      {workOrder.customer && <PaymentModal visible={paymentModalVisible} onClose={() => setPaymentModalVisible(false)} entityType="work_order" entityId={workOrder.id!} customerId={workOrder.customer.id!} customerEmail={workOrder.customer.email || ''} customerName={workOrder.customer.company_name || `${workOrder.customer.first_name} ${workOrder.customer.last_name}`} totalAmount={workOrder.total_amount} currentSummary={{ total_amount: workOrder.total_amount, amount_paid: workOrder.amount_paid || 0, balance_due: workOrder.balance_due || Math.max(0, workOrder.total_amount - (workOrder.amount_paid || 0)), payment_status: workOrder.payment_status || 'unpaid' }} onSuccess={handlePaymentSuccess} />}
      {workOrder.customer && <PaymentLinkModal visible={paymentLinkModalVisible} onCancel={() => setPaymentLinkModalVisible(false)} entityType="work_order" entityId={workOrder.id!} entityNumber={workOrder.wo_number} customerId={workOrder.customer.id!} customerEmail={workOrder.customer.email || ''} customerPhone={workOrder.customer.phone || ''} customerName={workOrder.customer.company_name || `${workOrder.customer.first_name} ${workOrder.customer.last_name}`} amount={workOrder.balance_due || workOrder.total_amount} description={`Work Order ${workOrder.wo_number} - ${assetLabel(workOrder.vehicle)}`} vehicleInfo={workOrder.vehicle ? assetLabel(workOrder.vehicle) : undefined} />}
      <Drawer title="Payment History" placement="right" width={isMobile ? '100%' : 720} onClose={() => setPaymentHistoryVisible(false)} open={paymentHistoryVisible}>
        <PaymentHistory entityType="work_order" entityId={workOrder.id!} showActions={true} showRefund={true} onUpdate={() => { loadWorkOrder(); message.success('Payment updated successfully'); }} />
      </Drawer>
      <InvoiceDetailModal visible={invoiceModalVisible} onClose={() => setInvoiceModalVisible(false)} invoice={invoice} onSendEmail={handleSendInvoiceEmail} onDownloadPDF={handleDownloadInvoicePDF} />
      {waiveDiagnosisItem && (
        <WaiveDiagnosisModal
          visible={!!waiveDiagnosisItem}
          diagnosisLineItem={waiveDiagnosisItem}
          approvedRepairLineItems={(workOrder.line_items || []).filter(
            (li) => !li.is_diagnosis && li.customer_approval_status === 'approved',
          )}
          loading={waiving}
          onConfirm={handleConfirmWaiveDiagnosis}
          onCancel={() => setWaiveDiagnosisItem(null)}
        />
      )}
      <QuickBookingDrawer
        visible={scheduleOpen}
        workOrderId={workOrder.id}
        onClose={() => setScheduleOpen(false)}
        onBooked={() => { setScheduleOpen(false); loadWorkOrder(); }}
      />
      {/* DVI template picker modal */}
      <Modal
        open={templatePickerOpen}
        title="Pick an inspection template"
        onCancel={() => setTemplatePickerOpen(false)}
        onOk={handleConfirmStartDVI}
        okButtonProps={{ disabled: !pickedTemplateId }}
      >
        <Select
          style={{ width: '100%' }} placeholder="Select template"
          value={pickedTemplateId}
          onChange={setPickedTemplateId}
          options={dviTemplates.map(t => ({ value: t.id, label: t.name }))}
        />
      </Modal>
      {/* Diagnosis loop — Create Repair Order: pick which lines to copy into a new repair WO */}
      <Modal
        open={spawnRepairOpen}
        title="Create Repair Order"
        onCancel={() => setSpawnRepairOpen(false)}
        onOk={handleSpawnRepair}
        okText="Create Repair Order"
        okButtonProps={{ loading: spawning, disabled: spawnSelectedIds.length === 0 }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <CancelBtn />
            <Button
              loading={spawning}
              disabled={spawnSelectedIds.length === 0}
              onClick={handleSpawnRepairEstimate}
              data-testid="spawn-repair-estimate"
            >
              Quote it first (estimate)
            </Button>
            <OkBtn />
          </div>
        )}
      >
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          Select the line items to carry into the repair.
        </p>
        <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
          {/* Names the money before the click, because the two buttons differ in
              what the customer sees, not in what work is carried. */}
          <strong>Quote it first</strong> creates an estimate the customer can approve,
          with the diagnosis they already paid for deducted.{' '}
          <strong>Create Repair Order</strong> skips the quote and starts the job now.
        </p>
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          value={spawnSelectedIds}
          onChange={(vals) => setSpawnSelectedIds(vals as number[])}
        >
          {(workOrder.line_items || []).map((li) => (
            <Checkbox key={li.id} value={li.id}>
              {li.concern || 'Untitled item'}
              {li.urgency && (
                <Tag color={li.urgency.color} style={{ marginLeft: 8 }}>{li.urgency.label}</Tag>
              )}
              {li.customer_approval_status === 'approved' && (
                <Tag color="success" style={{ marginLeft: 8 }}>approved</Tag>
              )}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Modal>
      {/* DVI tap-to-convert modal */}
      {convertItem && (
        <DVITapToConvertModal
          open
          item={convertItem}
          estimateId={workOrder.estimate_id}
          onCancel={() => setConvertItem(null)}
          onConvert={handleDVIConvert}
        />
      )}
    </div>
  );
};

export default OptimizedWorkOrderDetail;
